import * as Y from 'yjs';
import type { CommentMention } from '../mentions';

export type CommentReply = {
  id: string;
  body: string;
  createdAt: string;
  createdBy: string | null;
  mentions: CommentMention[];
};

export type CommentThread = {
  id: string;
  quote: string;
  createdAt: string;
  createdBy: string | null;
  resolved: boolean;
  replies: CommentReply[];
};

export function getCommentsMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap('docComments');
}

function mentionsToJson(value: unknown): CommentMention[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((m) => {
      if (!m || typeof m !== 'object') return null;
      const row = m as { id?: string; userId?: string; label?: string };
      const label = String(row.label || '').trim();
      if (!label) return null;
      return {
        id: row.id || row.userId || null,
        label,
      };
    })
    .filter((m): m is CommentMention => Boolean(m));
}

function repliesToJson(value: unknown): CommentReply[] {
  const replies: CommentReply[] = [];
  if (!(value instanceof Y.Array)) return replies;
  value.forEach((entry) => {
    if (!(entry instanceof Y.Map)) return;
    replies.push({
      id: String(entry.get('id') ?? ''),
      body: String(entry.get('body') ?? ''),
      createdAt: String(entry.get('createdAt') ?? ''),
      createdBy: (entry.get('createdBy') as string | null) ?? null,
      mentions: mentionsToJson(entry.get('mentions')),
    });
  });
  return replies;
}

function threadToJson(id: string, ymap: Y.Map<unknown>): CommentThread {
  return {
    id,
    quote: String(ymap.get('quote') ?? ''),
    createdAt: String(ymap.get('createdAt') ?? ''),
    createdBy: (ymap.get('createdBy') as string | null) ?? null,
    resolved: Boolean(ymap.get('resolved')),
    replies: repliesToJson(ymap.get('replies')),
  };
}

export function getCommentsState(doc: Y.Doc): CommentThread[] {
  const comments: CommentThread[] = [];
  getCommentsMap(doc).forEach((value, id) => {
    if (!(value instanceof Y.Map)) return;
    comments.push(threadToJson(id, value as Y.Map<unknown>));
  });
  comments.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  return comments;
}

function buildReply(params: {
  body: string;
  createdBy: string | null;
  createdAt: string;
  mentions?: CommentMention[];
}): Y.Map<unknown> {
  const ymap = new Y.Map<unknown>();
  ymap.set('id', crypto.randomUUID());
  ymap.set('body', params.body);
  ymap.set('createdAt', params.createdAt);
  ymap.set('createdBy', params.createdBy);
  const clean = (params.mentions || [])
    .map((m) => ({
      id: m?.id || null,
      label: String(m?.label || '').trim(),
    }))
    .filter((m) => m.label);
  ymap.set('mentions', clean);
  return ymap;
}

export function createComment(
  doc: Y.Doc,
  params: {
    quote?: string;
    body: string;
    createdBy?: string | null;
    mentions?: CommentMention[];
  },
): string {
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  const thread = new Y.Map<unknown>();
  thread.set('quote', params.quote ?? '');
  thread.set('createdAt', now);
  thread.set('createdBy', params.createdBy ?? null);
  thread.set('resolved', false);
  const replies = new Y.Array<Y.Map<unknown>>();
  replies.push([
    buildReply({
      body: params.body,
      createdBy: params.createdBy ?? null,
      createdAt: now,
      mentions: params.mentions,
    }),
  ]);
  thread.set('replies', replies);
  getCommentsMap(doc).set(id, thread);
  return id;
}

export function replyToComment(
  doc: Y.Doc,
  id: string,
  params: {
    body: string;
    createdBy?: string | null;
    mentions?: CommentMention[];
  },
): string | null {
  const thread = getCommentsMap(doc).get(id);
  if (!(thread instanceof Y.Map)) return null;
  const replies = thread.get('replies');
  if (!(replies instanceof Y.Array)) return null;
  const now = new Date().toISOString();
  const reply = buildReply({
    body: params.body,
    createdBy: params.createdBy ?? null,
    createdAt: now,
    mentions: params.mentions,
  });
  replies.push([reply]);
  return String(reply.get('id'));
}

export function deleteComment(doc: Y.Doc, id: string) {
  getCommentsMap(doc).delete(id);
}

export function setCommentResolved(doc: Y.Doc, id: string, resolved: boolean) {
  const thread = getCommentsMap(doc).get(id);
  if (!(thread instanceof Y.Map)) return;
  thread.set('resolved', resolved);
}

export function observeComments(
  doc: Y.Doc,
  onChange: (threads: CommentThread[]) => void,
): () => void {
  const map = getCommentsMap(doc);
  const sync = () => onChange(getCommentsState(doc));
  sync();
  map.observeDeep(sync);
  return () => map.unobserveDeep(sync);
}
